import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { NoteDemarche, NoteTheme, NotesStatistiques } from '../../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface NotesState {
  notes: NoteDemarche[];
  currentNote: NoteDemarche | null;
  statistiques: NotesStatistiques | null;
  loading: boolean;
  error: string | null;
  total: number;
}

const initialState: NotesState = {
  notes: [],
  currentNote: null,
  statistiques: null,
  loading: false,
  error: null,
  total: 0,
};

// Thunks asynchrones

/**
 * Récupérer toutes les notes d'une démarche
 */
export const fetchNotesByDemarche = createAsyncThunk(
  'notes/fetchNotesByDemarche',
  async (
    { demarcheId, theme, limit, offset }: { demarcheId: number; theme?: NoteTheme; limit?: number; offset?: number },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (theme) params.append('theme', theme);
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());

      const response = await axios.get(
        `${API_URL}/notes/demarche/${demarcheId}?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des notes');
    }
  }
);

/**
 * Récupérer les notes d'un thème spécifique
 */
export const fetchNotesByTheme = createAsyncThunk(
  'notes/fetchNotesByTheme',
  async ({ demarcheId, theme }: { demarcheId: number; theme: NoteTheme }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/notes/demarche/${demarcheId}/theme/${theme}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des notes');
    }
  }
);

/**
 * Récupérer une note spécifique
 */
export const fetchNoteById = createAsyncThunk(
  'notes/fetchNoteById',
  async (noteId: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.note;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération de la note');
    }
  }
);

/**
 * Créer une nouvelle note
 */
export const createNote = createAsyncThunk(
  'notes/createNote',
  async (
    noteData: { demarche_id: number; theme: NoteTheme; contenu: string; metadata?: any },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/notes`, noteData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.note;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la création de la note');
    }
  }
);

/**
 * Modifier une note
 */
export const updateNote = createAsyncThunk(
  'notes/updateNote',
  async (
    { noteId, updates }: { noteId: number; updates: { theme?: NoteTheme; contenu?: string; metadata?: any } },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/notes/${noteId}`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.note;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la modification de la note');
    }
  }
);

/**
 * Supprimer une note
 */
export const deleteNote = createAsyncThunk(
  'notes/deleteNote',
  async (noteId: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return noteId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la suppression de la note');
    }
  }
);

/**
 * Récupérer les statistiques des notes
 */
export const fetchNotesStatistiques = createAsyncThunk(
  'notes/fetchNotesStatistiques',
  async (demarcheId: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/notes/demarche/${demarcheId}/statistiques`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des statistiques');
    }
  }
);

// Slice Redux
const notesSlice = createSlice({
  name: 'notes',
  initialState,
  reducers: {
    clearNotes: (state) => {
      state.notes = [];
      state.currentNote = null;
      state.statistiques = null;
      state.error = null;
      state.total = 0;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch notes by demarche
    builder.addCase(fetchNotesByDemarche.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchNotesByDemarche.fulfilled, (state, action) => {
      state.loading = false;
      state.notes = action.payload.notes;
      state.total = action.payload.total;
    });
    builder.addCase(fetchNotesByDemarche.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch notes by theme
    builder.addCase(fetchNotesByTheme.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchNotesByTheme.fulfilled, (state, action) => {
      state.loading = false;
      state.notes = action.payload.notes;
    });
    builder.addCase(fetchNotesByTheme.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch note by ID
    builder.addCase(fetchNoteById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchNoteById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentNote = action.payload;
    });
    builder.addCase(fetchNoteById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create note
    builder.addCase(createNote.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createNote.fulfilled, (state, action) => {
      state.loading = false;
      state.notes = [action.payload, ...state.notes];
      state.total += 1;
    });
    builder.addCase(createNote.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update note
    builder.addCase(updateNote.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateNote.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.notes.findIndex((note) => note.id === action.payload.id);
      if (index !== -1) {
        state.notes[index] = action.payload;
      }
      if (state.currentNote?.id === action.payload.id) {
        state.currentNote = action.payload;
      }
    });
    builder.addCase(updateNote.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete note
    builder.addCase(deleteNote.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteNote.fulfilled, (state, action) => {
      state.loading = false;
      state.notes = state.notes.filter((note) => note.id !== action.payload);
      state.total -= 1;
      if (state.currentNote?.id === action.payload) {
        state.currentNote = null;
      }
    });
    builder.addCase(deleteNote.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch statistiques
    builder.addCase(fetchNotesStatistiques.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchNotesStatistiques.fulfilled, (state, action) => {
      state.loading = false;
      state.statistiques = action.payload;
    });
    builder.addCase(fetchNotesStatistiques.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearNotes, clearError } = notesSlice.actions;
export default notesSlice.reducer;
